import type { ComponentType } from 'react';
import { PERMISSIONS, type PermissionKey } from '@lims/permissions';
import type { DashboardSummary } from '../api/endpoints.js';
import {
  IconApproval,
  IconAudit,
  IconBilling,
  IconCatalogue,
  IconClients,
  IconDashboard,
  IconEquipment,
  IconFlask,
  IconInvoices,
  IconPayments,
  IconPricing,
  IconProfile,
  IconProjects,
  IconQuotations,
  IconReceipts,
  IconReports,
  IconReturned,
  IconReview,
  IconSamples,
  IconSettings,
  IconStorage,
  IconTasks,
  IconUsers,
  IconWorkOrders,
  type NavIconProps,
} from '../components/ui/nav-icons.js';

/**
 * The application's navigation, as data (D278).
 *
 * The approved redesign groups the destinations into six modules — Home,
 * Laboratory, Clients & orders, Reports, Equipment & quality, and Setup — in
 * the order the work actually happens, instead of one long rail. Only the
 * grouping changed: every destination, path and permission below is the one
 * the earlier rail used, so nobody gains or loses a door.
 *
 * Entries are filtered by the caller's permissions so people are not shown
 * doors they cannot open. That filtering is a COURTESY: every route behind
 * these links calls an endpoint that re-checks the same permission on the
 * server (CLAUDE.md section 5.1).
 */

export interface NavEntry {
  readonly to: string;
  readonly labelKey: string;
  readonly Icon: ComponentType<NavIconProps>;
  /** Omitted when authentication alone is enough. */
  readonly permission?: PermissionKey;
  /** Shown when the caller holds ANY of these. */
  readonly anyPermission?: readonly PermissionKey[];
  /** Development-only entries never reach a production bundle. */
  readonly isDevelopmentOnly?: boolean;
  /** Other path prefixes that belong to this entry (a record's own pages). */
  readonly alsoFor?: readonly string[];
  /** The live count beside the entry, from the dashboard summary. */
  readonly count?: (summary: DashboardSummary) => number | null | undefined;
}

export interface NavGroup {
  readonly id: string;
  readonly labelKey: string;
  readonly Icon: ComponentType<NavIconProps>;
  readonly entries: readonly NavEntry[];
  /** Setup sits at the foot of the sidebar, apart from the daily work. */
  readonly isFoot?: boolean;
}

export const NAV_GROUPS: readonly NavGroup[] = [
  {
    id: 'home',
    labelKey: 'nav.groupHome',
    Icon: IconDashboard,
    entries: [
      { to: '/home', labelKey: 'nav.dashboard', Icon: IconDashboard, permission: PERMISSIONS.DASHBOARD_VIEW },
      {
        to: '/my-tasks',
        labelKey: 'nav.myTasks',
        Icon: IconTasks,
        permission: PERMISSIONS.ASSIGNMENTS_VIEW,
        count: (summary) => summary.myTasks?.open,
      },
      /* D174: counts; money on it only for finance.view, decided by the server. */
      { to: '/dashboards', labelKey: 'nav.managementDashboard', Icon: IconDashboard, permission: PERMISSIONS.DASHBOARD_VIEW },
      { to: '/notifications', labelKey: 'nav.notifications', Icon: IconTasks },
    ],
  },
  {
    id: 'laboratory',
    labelKey: 'nav.groupLaboratory',
    Icon: IconFlask,
    entries: [
      /* D213: every handover stays reachable after the receiving desk moves on. */
      { to: '/samples/receipts', labelKey: 'nav.sampleReceipts', Icon: IconReceipts, permission: PERMISSIONS.SAMPLES_VIEW },
      {
        to: '/samples',
        labelKey: 'nav.samples',
        Icon: IconSamples,
        permission: PERMISSIONS.SAMPLES_VIEW,
        count: (summary) => summary.samples?.awaitingInspection,
      },
      {
        to: '/work',
        labelKey: 'nav.laboratoryWork',
        Icon: IconFlask,
        permission: PERMISSIONS.ASSIGNMENTS_VIEW,
        alsoFor: ['/attempts'],
        count: (summary) => summary.work?.unassigned,
      },
      /* Phase 9 (D97): entering, review and approval are three separate rights. */
      {
        to: '/returned',
        labelKey: 'nav.returnedWork',
        Icon: IconReturned,
        permission: PERMISSIONS.RESULTS_ENTER,
        count: (summary) => summary.returnedToMe,
      },
      {
        to: '/review',
        labelKey: 'nav.review',
        Icon: IconReview,
        permission: PERMISSIONS.RESULTS_REVIEW,
        count: (summary) => summary.awaitingReview,
      },
      {
        to: '/approval',
        labelKey: 'nav.approval',
        Icon: IconApproval,
        permission: PERMISSIONS.RESULTS_APPROVE,
        count: (summary) => summary.awaitingApproval,
      },
    ],
  },
  {
    id: 'orders',
    labelKey: 'nav.groupOrders',
    Icon: IconClients,
    entries: [
      { to: '/clients', labelKey: 'nav.clients', Icon: IconClients, permission: PERMISSIONS.CLIENTS_VIEW },
      { to: '/projects', labelKey: 'nav.projects', Icon: IconProjects, permission: PERMISSIONS.PROJECTS_VIEW },
      { to: '/quotations', labelKey: 'nav.quotations', Icon: IconQuotations, permission: PERMISSIONS.QUOTATIONS_VIEW },
      { to: '/work-orders', labelKey: 'nav.workOrders', Icon: IconWorkOrders, permission: PERMISSIONS.WORK_ORDERS_VIEW },
      { to: '/pricing-agreements', labelKey: 'nav.pricingAgreements', Icon: IconPricing, permission: PERMISSIONS.PRICING_AGREEMENTS_VIEW },
      /* Phase 11: finance, gated on `finance.view`; each act re-checked by its endpoint. */
      { to: '/billing', labelKey: 'nav.billing', Icon: IconBilling, permission: PERMISSIONS.FINANCE_VIEW },
      { to: '/invoices', labelKey: 'nav.invoices', Icon: IconInvoices, permission: PERMISSIONS.FINANCE_VIEW },
      { to: '/payments', labelKey: 'nav.payments', Icon: IconPayments, permission: PERMISSIONS.FINANCE_VIEW },
    ],
  },
  {
    id: 'reports',
    labelKey: 'nav.groupReports',
    Icon: IconReports,
    entries: [
      { to: '/reports', labelKey: 'nav.reports', Icon: IconReports, permission: PERMISSIONS.REPORTS_VIEW },
      { to: '/reports/new', labelKey: 'nav.prepareReport', Icon: IconReports, permission: PERMISSIONS.REPORTS_PREPARE },
      /* D112: the laboratory's own report templates. */
      { to: '/report-templates', labelKey: 'nav.reportTemplates', Icon: IconReports, permission: PERMISSIONS.TEMPLATES_VIEW },
    ],
  },
  {
    id: 'assets',
    labelKey: 'nav.groupAssets',
    Icon: IconEquipment,
    entries: [
      { to: '/equipment', labelKey: 'nav.equipment', Icon: IconEquipment, permission: PERMISSIONS.EQUIPMENT_VIEW },
      { to: '/maintenance', labelKey: 'nav.maintenance', Icon: IconEquipment, permission: PERMISSIONS.EQUIPMENT_VIEW },
      { to: '/maintenance/faults', labelKey: 'nav.faults', Icon: IconReturned, permission: PERMISSIONS.EQUIPMENT_VIEW },
      { to: '/storage', labelKey: 'nav.storage', Icon: IconStorage, permission: PERMISSIONS.STORAGE_MANAGE },
      /* D163–D168: a raise-only person still reaches the quality page, to raise. */
      {
        to: '/quality',
        labelKey: 'nav.quality',
        Icon: IconReview,
        anyPermission: [PERMISSIONS.QUALITY_VIEW, PERMISSIONS.QUALITY_RAISE],
      },
      { to: '/corrective-actions', labelKey: 'nav.correctiveActions', Icon: IconTasks, permission: PERMISSIONS.QUALITY_VIEW },
    ],
  },
  {
    id: 'setup',
    labelKey: 'nav.groupSetup',
    Icon: IconSettings,
    isFoot: true,
    entries: [
      { to: '/catalogue', labelKey: 'nav.catalogue', Icon: IconCatalogue, permission: PERMISSIONS.CATALOGUE_VIEW },
      { to: '/users', labelKey: 'nav.users', Icon: IconUsers, permission: PERMISSIONS.USERS_VIEW },
      { to: '/settings', labelKey: 'nav.settings', Icon: IconSettings, permission: PERMISSIONS.SETTINGS_VIEW },
      /* D175: all-or-nothing import of existing records. */
      { to: '/settings/import', labelKey: 'nav.dataImport', Icon: IconSettings, permission: PERMISSIONS.DATA_IMPORT },
      { to: '/audit', labelKey: 'nav.auditLog', Icon: IconAudit, permission: PERMISSIONS.AUDIT_VIEW },
      { to: '/design-system', labelKey: 'nav.designSystem', Icon: IconProfile, isDevelopmentOnly: true },
    ],
  },
];

/** Whether `pathname` is `prefix` itself or a page beneath it. */
function isUnder(pathname: string, prefix: string): boolean {
  return pathname === prefix || pathname.startsWith(`${prefix}/`);
}

/**
 * The ONE entry the current page belongs to: the visible entry whose path is
 * the longest prefix of the location. Longest-prefix rather than "every
 * matching link", so `/samples/receipts` lights Sample Receipts and not also
 * Samples, and `/reports/new` lights Prepare report and not also Reports.
 */
export function activeEntry(
  groups: readonly NavGroup[],
  pathname: string,
): { group: NavGroup; entry: NavEntry } | null {
  let best: { group: NavGroup; entry: NavEntry; length: number } | null = null;
  for (const group of groups) {
    for (const entry of group.entries) {
      for (const prefix of [entry.to, ...(entry.alsoFor ?? [])]) {
        if (isUnder(pathname, prefix) && (!best || prefix.length > best.length)) {
          best = { group, entry, length: prefix.length };
        }
      }
    }
  }
  return best ? { group: best.group, entry: best.entry } : null;
}
